import { decodeJwtPayload, getAuthToken } from 'utils/auth';
import { BACKOFFICE_API_BASE } from 'utils/urls';

/**
 * Sync the player's `enableSecureLogin` profile attribute.
 *
 * Used by the post-login enrollment prompt (`useBiometricEnrollmentOffer`) —
 * the two Settings toggles (`ProfilePanel`/`AccountMenuPanel`) each keep their
 * own local copy of this same mutation rather than importing this one, since
 * they derive `userId` slightly differently. See EN-5415 branch review for
 * why that's not being consolidated in this pass.
 *
 * Returns whether the account-level flag was actually confirmed updated.
 * Callers must not treat the toggle as settled on `false` — the device
 * credential and the account flag disagreeing is what leaves a player with
 * a biometric login the next attempt then fails, so a caller whose credential
 * creation this gated must roll that credential back on `false`.
 *
 * `intent: 'decline'` marks "Not now" on an enrollment offer: the server
 * records it without revoking sessions, and leaves an account already enabled
 * on another device as it is. Without it, `enabled=false` is a real disable,
 * which revokes every session (EN-10901).
 */
export async function updateSecureLoginFlag(
  enabled: boolean,
  playerGUID?: string,
  intent?: 'decline'
): Promise<boolean> {
  const { access_token } = getAuthToken();
  let userId = playerGUID;
  if (!userId && access_token) {
    try {
      const payload = decodeJwtPayload(access_token);
      userId = payload.sub || '';
    } catch (_e) {
      /* ignore — handled by the guard below */
    }
  }
  if (!userId || !access_token) return false;

  let accountEnabled = enabled;
  try {
    const response = await fetch(`${BACKOFFICE_API_BASE}/descope/updateSecureLogin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        Authorization: `Bearer ${access_token}`
      },
      body: new URLSearchParams({
        userId,
        enableSecureLogin: enabled ? '1' : '0',
        ...(intent ? { intent } : {})
      }).toString()
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.success !== true) return false;
    // A decline leaves an account enabled elsewhere at `true`; store what the
    // account actually holds, not what was asked for.
    if (typeof body.enableSecureLogin === 'boolean') accountEnabled = body.enableSecureLogin;
  } catch (err) {
    console.error('[Biometrics] Failed to sync enableSecureLogin:', err);
    return false;
  }

  try {
    localStorage.setItem('hj_biometric_enabled', String(accountEnabled));
  } catch (_e) {
    /* ignore */
  }
  return true;
}
